import { fabric } from 'fabric';
import { groupOf, wholeGroup, withMates } from '../editor/arrange';
import type { Editor } from '../editor/editor';

/*
 * จัดกลุ่ม, choice ก: every object stays its own object in the saved work; members share a `groupId` (EXTRA_PROPS).
 * So PDF/print checks/export/แต่งภาพ/ครอป/ลบพื้นหลัง/video see plain objects, as before. Here the canvas treats them as one:
 *   tap a member            → the whole group is chosen, and the same press can drag it (Fabric is handed the group)
 *   tap the chosen group    → only the member under the finger (to edit its text or picture); members of that
 *                             group are then chosen one by one until something else is chosen
 *   box/Shift selection     → whole groups are added (and taken out) together
 * Hidden and locked members are left out of the selection (they cannot be chosen anyway).
 */

type G = fabric.Object & { groupId?: string };
type Opt = { e?: Event; target?: fabric.Object; deselected?: fabric.Object[] };

const TAP_PX = 8, TAP_MS = 600;

export function initGroupSelect(ed: Editor) {
  const { canvas } = ed;
  /** the group whose members are being chosen one by one (after a second tap), or null */
  let inside: string | null = null;
  const pickable = () => ed.userObjects().filter(o => o.selectable !== false && o.visible !== false) as G[];
  const listOf = (a: fabric.Object | null | undefined): G[] => !a ? [] : a.type === 'activeSelection' ? (a as fabric.ActiveSelection).getObjects() as G[] : [a as G];

  function select(list: G[]) {
    canvas.discardActiveObject();
    if (list.length > 1) canvas.setActiveObject(new fabric.ActiveSelection(list, { canvas }));
    else if (list.length) canvas.setActiveObject(list[0]);
    ed.paint(); canvas.requestRenderAll();
  }

  /** choose `o` with the rest of its group (object menu); false when it is not in a group */
  function choose(o: fabric.Object): boolean {
    const list = withMates([o as G], pickable());
    if (list.length < 2) return false;
    inside = null; select(list);
    return true;
  }

  /* a press: on a member → hand Fabric the whole group (so one press chooses and drags it);
     on the chosen group → remember it, a tap (no drag) then chooses the member under the finger */
  let press: { x: number; y: number; t: number; sel: fabric.Object } | null = null;
  canvas.on('mouse:down:before', (opt: Opt) => {
    press = null;
    const t = opt.target as G | undefined, e = opt.e as MouseEvent | undefined;
    if (!t || !e || (canvas.getActiveObject() as fabric.IText | null)?.isEditing) return;
    if (t.type === 'activeSelection') {
      if (wholeGroup(listOf(t), pickable())) {
        const p = canvas.getPointer(e, true);
        press = { x: p.x, y: p.y, t: e.timeStamp, sel: t };
      }
      return;
    }
    // Shift-click: Fabric adds/removes it; the selection events below make it the whole group
    const key = canvas.selectionKey as string | string[] | undefined;
    if ((Array.isArray(key) ? key : [key ?? 'shiftKey']).some(k => (e as unknown as Record<string, boolean>)[k])) return;
    const g = groupOf(t);
    if (!g || g === inside || t.selectable === false) return;
    const list = withMates([t], pickable());
    if (list.length < 2) return;
    inside = null;
    canvas.discardActiveObject();
    const sel = new fabric.ActiveSelection(list, { canvas });
    canvas.setActiveObject(sel);
    // Fabric reads its target after this event: the group is what gets chosen and dragged by this press
    (canvas as unknown as { _target: fabric.Object })._target = sel;
    ed.paint();
  });

  // a second finger (two-finger tap/pinch, src/editor/editor.ts): the first finger's press is not a tap (BUG-42) —
  // when it lifts first, Fabric would see a tap and choose one member before the menu opens
  document.addEventListener('touchstart', e => { if (e.touches.length > 1) press = null; }, { capture: true, passive: true });

  canvas.on('mouse:up', (opt: Opt) => {
    const pr = press; press = null;
    if (!pr || !opt.e || canvas.getActiveObject() !== pr.sel || (pr.sel as { __corner?: string }).__corner) return;
    const p = canvas.getPointer(opt.e as MouseEvent, true);
    if (Math.hypot(p.x - pr.x, p.y - pr.y) > TAP_PX || opt.e.timeStamp - pr.t > TAP_MS) return;   // a drag, or held
    const members = listOf(pr.sel);
    canvas.discardActiveObject();
    const at = new fabric.Point(p.x, p.y);
    // the top-most object under the finger: a member (→ that one alone), or another object in a gap of the group
    const top = pickable().reverse().find(o => { o.setCoords(); return o.containsPoint(at); });
    if (!top) { select(members); return; }      // an empty gap between members: the group stays chosen
    if (!members.includes(top)) { if (!choose(top)) select([top]); return; }
    inside = groupOf(top);
    select([top]);
  });

  /* box selection and Shift-click: whole groups in, whole groups out (only what the person did: events with `e`) */
  function complete(opt: Opt) {
    const now = listOf(canvas.getActiveObject());
    // one-by-one ends as soon as anything but a single member of that group is chosen — Fabric gives no
    // selection:cleared when one object replaces another (tap, layers list), only selection:updated (BUG-43)
    if (inside && !(now.length === 1 && groupOf(now[0]) === inside)) inside = null;
    if (!opt.e) return;
    if (now.length === 1 && inside && groupOf(now[0]) === inside) return;
    const list = withMates(now, pickable(), (opt.deselected ?? []) as G[]);
    if (list.length === now.length && list.every(o => now.includes(o))) return;
    if (!list.length) { canvas.discardActiveObject(); canvas.requestRenderAll(); return; }
    select(list);
  }
  canvas.on('selection:created', complete);
  canvas.on('selection:updated', complete);
  canvas.on('selection:cleared', () => { inside = null; });

  return { choose };
}

export type GroupSelect = ReturnType<typeof initGroupSelect>;
